import { APP_CONSTANTS } from '@/config/constants';
import { reloadAppAsync } from 'expo';
import { databaseService } from '@/services/databaseService';
import {
  SUPERSEDED_BY_REMOTE_ENTITY,
  accountedArchiveIdFor,
  casExpectedFromArchive,
} from '@/services/sync/PendingOperationTerminalization';
import { digestRecoveryPendingRow } from './recoveryPendingDigest';
import type {
  PendingRecoveryItem,
  RecoveryResetContext,
  SafeResetExecutor,
} from '@/types/recovery';

interface RecoveryResetDatabase {
  readItem<T>(collection: string, id: string): Promise<T | null>;
  createItem<T>(collection: string, item: T, skipPendingOperation?: boolean): Promise<string>;
  removePendingOperationIfUnchanged?(
    id: string,
    expected: {
      operation: string;
      collection: string;
      item_id: string;
      data: string | null;
      timestamp: number;
    }
  ): Promise<boolean>;
  deleteItemIfUnchanged?(
    collection: string,
    id: string,
    expected: Record<string, unknown>,
    skipPendingOperation?: boolean
  ): Promise<boolean>;
  clearAllDataForRecovery(context: RecoveryResetContext): Promise<boolean>;
  requiresRuntimeRestart?(): boolean;
}

interface AppSafeResetDependencies {
  database?: RecoveryResetDatabase;
  verifyCanonicalReady(context: RecoveryResetContext): Promise<void>;
  pullCanonical(context: RecoveryResetContext): Promise<void>;
  restoreHistorical(context: RecoveryResetContext): Promise<void>;
  verifyRestored(context: RecoveryResetContext): Promise<{
    valid: boolean;
    unaccountedPendingCount: number;
    details?: Record<string, number | string | boolean | null>;
  }>;
  now?: () => Date;
}

const TERMINAL = new Set([
  'server_applied',
  'historical_materialized',
  'conflict_materialized',
  'escrowed_verified',
  'superseded_by_remote_entity',
]);

const serialize = (value: unknown): string | null => {
  if (value === undefined || value === null) return null;
  return typeof value === 'string' ? value : JSON.stringify(value);
};

export class AppSafeResetExecutor implements SafeResetExecutor {
  private readonly database: RecoveryResetDatabase;
  private readonly now: () => Date;

  constructor(private readonly dependencies: AppSafeResetDependencies) {
    this.database = (dependencies.database ?? databaseService) as RecoveryResetDatabase;
    this.now = dependencies.now ?? (() => new Date());
  }

  async archiveAccountedPending(context: RecoveryResetContext): Promise<void> {
    for (const item of context.pendingItems) {
      if (!TERMINAL.has(item.classification)) {
        throw new Error(`Pendencia nao contabilizada: ${item.pendingId}.`);
      }
      await this.archiveOne(context, item);
    }
  }

  async clearLocalData(context: RecoveryResetContext): Promise<void> {
    if (!(await this.database.clearAllDataForRecovery(context))) {
      throw new Error('Limpeza local autorizada nao foi concluida.');
    }
  }

  async ensureFreshRuntime(): Promise<void> {
    if (!this.database.requiresRuntimeRestart?.()) return;
    try { await reloadAppAsync('recovery-reset'); } catch { /* Reabertura manual continua possível. */ }
    throw new Error('database_reset_restart_required');
  }

  verifyCanonicalReady(context: RecoveryResetContext): Promise<void> {
    return this.dependencies.verifyCanonicalReady(context);
  }

  pullCanonicalState(context: RecoveryResetContext): Promise<void> {
    return this.dependencies.pullCanonical(context);
  }

  restoreHistoricalState(context: RecoveryResetContext): Promise<void> {
    return this.dependencies.restoreHistorical(context);
  }

  verifyRestoredState(context: RecoveryResetContext) {
    return this.dependencies.verifyRestored(context);
  }

  private async archiveOne(
    context: RecoveryResetContext,
    item: PendingRecoveryItem
  ): Promise<void> {
    const archiveTable = APP_CONSTANTS.DATABASE.TABLES.ACCOUNTED_PENDING_OPERATIONS.NAME;
    const prefixed = item.pendingId.match(/^(pending_operations|pending_deletions):(.+)$/);
    const sourceQueue: 'pending_operations' | 'pending_deletions' =
      prefixed?.[1] === 'pending_deletions' ? 'pending_deletions' : 'pending_operations';
    const sourceId = prefixed?.[2] ?? item.pendingId;
    const archiveId = item.classification === SUPERSEDED_BY_REMOTE_ENTITY
      ? accountedArchiveIdFor(sourceId, item.sourceDigest)
      : `${context.recoveryId}:${item.pendingId}`;
    if (
      item.classification === SUPERSEDED_BY_REMOTE_ENTITY
      && item.evidenceRef !== archiveId
    ) {
      throw new Error(`evidenceRef de supersessao diverge do arquivo canonico: ${item.pendingId}.`);
    }
    const existingArchive = await this.database.readItem<Record<string, any>>(
      archiveTable,
      archiveId
    );
    const pendingOperation = sourceQueue === 'pending_operations'
      ? await this.database.readItem<Record<string, any>>(
          APP_CONSTANTS.DATABASE.TABLES.PENDING_OPERATIONS.NAME,
          sourceId
        )
      : null;
    const pendingDeletion = sourceQueue === 'pending_deletions'
      ? await this.database.readItem<Record<string, any>>(
          APP_CONSTANTS.DATABASE.TABLES.PENDING_DELETIONS.NAME,
          sourceId
        )
      : null;
    const source = pendingOperation ?? pendingDeletion;
    if (
      source
      && digestRecoveryPendingRow(sourceQueue, source) !== item.sourceDigest
    ) {
      throw new Error(`Digest atual diverge da pendencia assinada: ${item.pendingId}.`);
    }
    if (item.classification === SUPERSEDED_BY_REMOTE_ENTITY) {
      await this.archiveSuperseded(context, item, {
        archiveId,
        existingArchive,
        source,
        sourceQueue,
        sourceId,
      });
      return;
    }
    if (existingArchive) {
      if (
        existingArchive.empresa_id !== context.identity.companyId ||
        existingArchive.user_id !== context.identity.userId ||
        existingArchive.receipt_id !== context.receipt.receipt.receiptId ||
        existingArchive.payload_sha512 !== item.sourceDigest ||
        existingArchive.accounting_state !== item.classification
      ) {
        throw new Error(`Arquivo contabilizado diverge da pendencia: ${item.pendingId}.`);
      }
      if (!source) return;
      await this.removeSourcePending(sourceQueue, sourceId, item.pendingId, source);
      return;
    }
    if (!source) {
      throw new Error(`Evidencia local da pendencia ausente: ${item.pendingId}.`);
    }

    const accountedAt = this.now().getTime();
    await this.database.createItem(archiveTable, {
      id: archiveId,
      empresa_id: context.identity.companyId,
      user_id: context.identity.userId,
      operation: String(source.operation ?? (pendingDeletion ? 'storage_delete' : 'unknown')),
      collection: String(source.collection ?? (pendingDeletion ? 'pending_deletions' : 'unknown')),
      item_id: String(source.itemId ?? source.item_id ?? source.id ?? item.pendingId),
      data: serialize(source.data ?? source),
      original_timestamp: Number(source.timestamp ?? source.created_at ?? accountedAt),
      accounting_state: item.classification,
      reason: item.reason,
      receipt_id: context.receipt.receipt.receiptId,
      payload_sha512: item.sourceDigest,
      accounted_at: accountedAt,
      created_at: accountedAt,
      updated_at: accountedAt,
    }, true);

    await this.removeSourcePending(sourceQueue, sourceId, item.pendingId, source);
  }

  private async archiveSuperseded(
    context: RecoveryResetContext,
    item: PendingRecoveryItem,
    input: {
      archiveId: string;
      existingArchive: Record<string, any> | null;
      source: Record<string, any> | null;
      sourceQueue: 'pending_operations' | 'pending_deletions';
      sourceId: string;
    }
  ): Promise<void> {
    const archive = input.existingArchive;
    if (!archive) {
      throw new Error(
        `Pendencia superseded exige arquivo local e ledger previos: ${item.pendingId}.`
      );
    }
    if (
      archive.empresa_id !== context.identity.companyId ||
      archive.user_id !== context.identity.userId ||
      archive.receipt_id !== context.receipt.receipt.receiptId ||
      archive.payload_sha512 !== item.sourceDigest ||
      archive.accounting_state !== SUPERSEDED_BY_REMOTE_ENTITY
    ) {
      throw new Error(`Evidencia de supersessao diverge: ${item.pendingId}.`);
    }
    if (!input.source) return;
    await this.removeSourcePending(
      input.sourceQueue,
      input.sourceId,
      item.pendingId,
      input.source,
      input.sourceQueue === 'pending_operations'
        ? casExpectedFromArchive(archive as any)
        : undefined
    );
  }

  private async removeSourcePending(
    sourceQueue: 'pending_operations' | 'pending_deletions',
    sourceId: string,
    pendingId: string,
    source: Record<string, any>,
    pendingOperationExpected?: {
      operation: string;
      collection: string;
      item_id: string;
      data: string | null;
      timestamp: number;
    }
  ): Promise<void> {
    if (sourceQueue === 'pending_operations') {
      if (!this.database.removePendingOperationIfUnchanged) {
        throw new Error('CAS indisponivel para pending_operations.');
      }
      const expected = pendingOperationExpected ?? {
        operation: String(source.operation ?? ''),
        collection: String(source.collection ?? ''),
        item_id: String(source.itemId ?? source.item_id ?? ''),
        data: serialize(source.data),
        timestamp: Number(source.timestamp),
      };
      if (!await this.database.removePendingOperationIfUnchanged(sourceId, expected)) {
        throw new Error(`Fila ativa mudou; reset bloqueado: ${pendingId}.`);
      }
      return;
    }

    if (!this.database.deleteItemIfUnchanged) {
      throw new Error('CAS indisponivel para pending_deletions.');
    }
    const removed = await this.database.deleteItemIfUnchanged(
      APP_CONSTANTS.DATABASE.TABLES.PENDING_DELETIONS.NAME,
      sourceId,
      source,
      true
    );
    if (!removed) throw new Error(`Fila ativa mudou; reset bloqueado: ${pendingId}.`);
  }
}
